import type { MessagesFilter } from '../../pages/private/Contacts/contacts.constants';
import {
  Person,
  PersonActivity,
  PersonEvent,
} from '../../services/crm.service';

export const CONTACT_SCOPE = {
  PERSONAL: 'personal',
  TEAM: 'team',
} as const;

export type ContactScope = (typeof CONTACT_SCOPE)[keyof typeof CONTACT_SCOPE];
export type OriginFilter =
  | 'all'
  | 'google'
  | 'outlook'
  | 'slack'
  | 'landingPage';
export type NotesStatus = 'idle' | 'saving' | 'saved';

export interface ContactsState {
  scope: ContactScope;
  query: string;
  originFilter: OriginFilter;
  people: Person[];
  cursor: string | null;
  hasMore: boolean;
  isFetchingMore: boolean;
  isLoadingPeople: boolean;
  peopleError: string | null;
  searchResults: Person[] | null;
  isSearching: boolean;
  searchError: string | null;
  selectedPersonId: string | null;
  messages: PersonActivity[];
  messagesPersonId: string | null;
  messagesCursor: string | null;
  messagesGeneration: number;
  hasMoreMessages: boolean;
  messagesCapped: boolean;
  isLoadingMessages: boolean;
  isLoadingMoreMessages: boolean;
  messagesFilter: MessagesFilter;
  recentlyInsertedMessageKeys: string[];
  removedMessageKeys: string[];
  events: PersonEvent[];
  eventsPersonId: string | null;
  eventsCursor: string | null;
  eventsGeneration: number;
  hasMoreEvents: boolean;
  isLoadingEvents: boolean;
  isLoadingMoreEvents: boolean;
  recentlyInsertedEventIds: string[];
  removedEventIds: string[];
  locallyRemovedEventIds: string[];
  notesStatus: NotesStatus;
  isCreateOpen: boolean;
  editPersonId: string | null;
  deleteTargetId: string | null;
  deepLinkPerson: Person | null;
  isResolvingDeepLink: boolean;
}

export interface ContactsActions {
  setScope: (scope: ContactScope) => void;
  setQuery: (query: string) => void;
  setOriginFilter: (filter: OriginFilter) => void;
  setPeoplePage: (people: Person[], cursor: string | null) => void;
  appendPeoplePage: (people: Person[], cursor: string | null) => void;
  setFetchingMore: (isFetchingMore: boolean) => void;
  resetList: () => void;
  setLoadingPeople: (isLoading: boolean) => void;
  setPeopleError: (error: string | null) => void;
  setSearchResults: (results: Person[] | null) => void;
  setSearching: (isSearching: boolean) => void;
  setSearchError: (error: string | null) => void;
  clearSearch: () => void;
  selectPerson: (personId: string | null) => void;
  upsertPerson: (person: Person) => void;
  applyCreatedPerson: (person: Person) => void;
  removePerson: (personId: string) => void;
  applyMergeResult: (person: Person, mergedIds: string[]) => void;
  setMessages: (
    personId: string,
    items: PersonActivity[],
    cursor?: string | null,
  ) => void;
  appendMessages: (
    personId: string,
    generation: number,
    items: PersonActivity[],
    cursor: string | null,
  ) => void;
  prependMessages: (personId: string, items: PersonActivity[]) => void;
  setLoadingMessages: (isLoading: boolean) => void;
  setLoadingMoreMessages: (isLoading: boolean) => void;
  setMessagesFilter: (filter: MessagesFilter) => void;
  markMessagesArrived: (keys: string[]) => void;
  clearMessageArrived: (key: string) => void;
  removeMessage: (personId: string, messageKey: string) => void;
  setEvents: (
    personId: string,
    items: PersonEvent[],
    cursor: string | null,
  ) => void;
  appendEvents: (
    personId: string,
    generation: number,
    items: PersonEvent[],
    cursor: string | null,
  ) => void;
  prependEvents: (personId: string, items: PersonEvent[]) => void;
  insertEvent: (personId: string, event: PersonEvent) => void;
  updateEvent: (personId: string, event: PersonEvent) => void;
  removeEvent: (personId: string, eventId: string) => void;
  setLoadingEvents: (isLoading: boolean) => void;
  setLoadingMoreEvents: (isLoading: boolean) => void;
  markEventsArrived: (ids: string[]) => void;
  clearEventArrived: (id: string) => void;
  markEventRemovedRemotely: (eventId: string) => void;
  clearEventRemoved: (eventId: string) => void;
  markEventRemovedLocally: (eventId: string) => void;
  clearEventRemovedLocally: (eventId: string) => void;
  setNotesStatus: (status: NotesStatus) => void;
  openCreate: () => void;
  closeCreate: () => void;
  openEdit: (personId: string) => void;
  closeEdit: () => void;
  setDeleteTarget: (personId: string | null) => void;
  setDeepLinkPerson: (person: Person | null) => void;
  setResolvingDeepLink: (isResolving: boolean) => void;
  clearDeepLink: () => void;
  reset: () => void;
}

export type ContactsStore = ContactsState & ContactsActions;
